import React from 'react';
import useScrollReveal from '../hooks/useScrollReveal';

const milestones = [
  { year: '2016', label: 'Company Founded' },
  { year: '2018', label: 'First Major Project' },
  { year: '2021', label: 'MahaMumbai Expansion' },
  { year: '2024', label: '15 Expert Team' },
  { year: '2026', label: 'Mumbai-Pune-Sambhajinagar' },
];

const AboutDeveloper = () => {
  const [headRef, headVisible] = useScrollReveal();
  const [contentRef, contentVisible] = useScrollReveal({ threshold: 0.15 });
  const [ctaRef, ctaVisible] = useScrollReveal({ threshold: 0.2 });

  return (
    <section id="about" className="py-24 lg:py-32 bg-gray-100 relative overflow-hidden border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header — left aligned */}
        <div ref={headRef} className={`max-w-2xl mb-16 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-[11px] tracking-[0.3em] uppercase font-semibold text-accent-500 mb-5">
            Our Story
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading text-dark-800 leading-tight">
            About{' '}
            <span className="italic text-primary-600">Prime Acres</span>
          </h2>
        </div>

        <div ref={contentRef} className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Left: Image */}
          <div className={`relative transition-all duration-1000 ${contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="relative overflow-hidden rounded-2xl group">
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"
                alt="Modern architecture"
                className="w-full h-[400px] lg:h-[480px] object-cover group-hover:scale-105 transition-transform duration-[2000ms]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-white/50 text-[10px] tracking-[0.2em] uppercase mb-1">Established</p>
                <p className="text-white text-3xl font-heading">10+ Years</p>
              </div>
            </div>
          </div>

          {/* Right: Info */}
          <div className={`transition-all duration-1000 delay-200 ${contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <p className="text-dark-500 leading-[2] text-[15px] font-light mb-5">
              Welcome to Prime Acres, where our mission is to help you Plot your future. With over
              10 years of trusted real estate experience, we specialize in premium land parcels and
              residential flats across Mumbai, Pune, and Sambhajinagar.
            </p>
            <p className="text-dark-400 leading-[2] text-[15px] font-light mb-10">
              Backed by a dedicated team of 15 industry experts, we provide comprehensive,
              end-to-end guidance — from plot selection and legal verification to registration and possession.
            </p>

            {/* Milestones */}
            <div className="mb-8">
              <p className="text-[10px] tracking-[0.2em] uppercase text-dark-300 font-semibold mb-5">Our Journey</p>
              <div className="flex items-start gap-0 overflow-x-auto pb-2">
                {milestones.map((m, i) => (
                  <div key={i} className="flex items-center flex-shrink-0">
                    <div className="text-center group cursor-default">
                      <div className="w-3 h-3 rounded-full bg-primary-200 group-hover:bg-accent-400 transition-colors duration-300 mx-auto mb-2" />
                      <p className="text-sm font-heading text-primary-600">{m.year}</p>
                      <p className="text-[9px] text-dark-300 tracking-wide max-w-[70px] leading-tight mt-0.5">{m.label}</p>
                    </div>
                    {i < milestones.length - 1 && (
                      <div className="w-8 md:w-12 h-px bg-gray-200 mx-1 flex-shrink-0 mt-1.5" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div ref={ctaRef} className={`mt-16 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-primary-600 rounded-2xl p-10 md:p-14 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

            <div className="relative flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <h3 className="text-3xl md:text-4xl font-heading text-white mb-3 leading-snug">
                  <span className="italic text-accent-300">"Plot Your</span> Future<br className="hidden md:block" /> With Prime Acres"
                </h3>
                <p className="text-white/50 text-sm leading-relaxed max-w-lg font-light">
                  We don't just sell land — we help you capitalize on the future of Mumbai's expansion.
                </p>
              </div>
              <button onClick={() => window.dispatchEvent(new Event('openContactModal'))} className="flex-shrink-0 bg-white text-primary-700 text-[11px] font-semibold tracking-[0.15em] uppercase py-4 px-10 rounded-full hover:bg-accent-50 hover:shadow-lg transition-all duration-500">
                Enquire Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutDeveloper;
